// AppUsersApiProvider — mounts UsersApiProvider with a module-level singleton
// client so user hooks (menu, settings, access guards) work without per-route wiring.

import { type ReactNode } from 'react';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import { UsersApiProvider } from '@/entities/user';
import type { UsersApiClient } from '@/entities/user';

const authProvider = { getToken };

let _usersClient: UsersApiClient | null = null;

function getUsersClient(): UsersApiClient {
  if (!_usersClient) {
    const baseUrl = readEnv('VITE_API_BASE_URL') ?? '';
    const client = createApiClient({ baseUrl, auth: authProvider });
    _usersClient = {
      fetchCurrent: (signal) => client.request({ path: '/api/v1/auth/me', signal }),
      searchOptions: (search, signal) =>
        client.request({
          path: '/api/v1/admin/users',
          query: { q: search },
          signal,
        }),
    };
  }
  return _usersClient;
}

export interface AppUsersApiProviderProps {
  children: ReactNode;
  /** Override the API client — intended for tests only. */
  client?: UsersApiClient;
}

export function AppUsersApiProvider({ children, client }: AppUsersApiProviderProps) {
  return (
    <UsersApiProvider client={client ?? getUsersClient()}>
      {children}
    </UsersApiProvider>
  );
}
